import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs/Rx';
import 'rxjs/add/operator/map';
import { ReplaySubject, Subject } from 'rxjs/';

import { CommunicationService } from './communication.service';
import { Study } from '../models/study';
import { User } from '../models/user';
import { BasicError, Error403 } from '../models/error/error';

@Injectable()
export class GroupService {


  /*
    All Vars for getAllGroups function
   */
  private allGroupsUrl = '/api/v1/studies';
  private allGroupsReplay: Subject<any> = null;     // ReplaySubject of getAllGroups
  private allGroupsThreshold = 0;                   // Until this time the cached all Groups will be returned
  private allGroupsAddTime = 1 * 1000;         // 4 minutes threshold should be enough
  private allGroupsLoaded = false;                  // Set is true if all Groups was loaded once
  private allGroups: Study[] = [];
  private amountAllGroups: Number = 0;

  /*
    All Vars for getMyGroups function
   */
  private myGroupsUrl = '/api/v1/my/studies';
  private myGroupsReplay: Subject<any> = null;      // ReplaySubject of getAllGroups
  private myGroupsThreshold = 0;                    // Until this time the cached all Groups will be returned
  private myGroupsAddTime = 1 * 1000;               // 4 minutes threshold should be enough
  private myGroupsLoaded = false;                   // Set is true if all Groups was loaded once
  private myGroups: Study[] = [];
  private amountMyGroups: Number = 0;
  private sumOfMyStudies: Number = 0;

  /*
    All Vars for getting one group
  */
  private groupDetailsUrl = '/api/v1/studies/';
  private groupDetailsReplay: Subject<any> = null;

  /**
   * Vars for subscribe to a Group
   */
  private subscribeToGroupUrl = '/api/v1/studies/##id##/subscribe';
  private subscribeToGroupReplay = null;

  /**
   * Vars for unsubscribe from a Group
   */
  private unsubscribeFromGroupUrl = '/api/v1/studies/##id##/unsubscribe';
  private unsubscribeFromGroupReplay = null;

  // Vars for getting collaborators of study
  private studyCollaboratorsURL = '/api/v1/studies/##id##?include=collaborators&limit=0';


  constructor(private communication: CommunicationService) { }

  /**
   * This function returns all Groups for each user.
   * @return ReplaySubject<any> - can contain error
   */
  public getAllGroups(currentPage: number = 1): Subject<any> {
    if (this.allGroupsReplay === null) {
      this.allGroupsReplay = new Subject();
    }

    if (this.allGroupsLoaded && this.allGroupsThreshold > (new Date().getTime())) {
      const tempReplay = new ReplaySubject(1);
      tempReplay.next(this.allGroups);
      return tempReplay;
    }

    // No threshold data found, the data will be fetched from api
    const request = this.communication.get(this.allGroupsUrl + '?page=' + currentPage + '&limit=0');
    request.subscribe(json => {
      // If an Error returned?
      if (json instanceof BasicError) {
        this.allGroupsThreshold = 0;
        this.allGroupsReplay.next(json);
        return;
      }

      // No Error occured
      this.amountAllGroups = json.meta.pagination.total;
      const total: number = json.meta.pagination.total_pages;
      const retArr: Study[] = [];

      for (let i = 0; i < json.data.length; i++) {
        const stdJSON = json.data[i];
        const study = new Study(stdJSON.id);
        if (stdJSON.id) { study.setId(stdJSON.id) };
        if (stdJSON.attributes.name) { study.setName(stdJSON.attributes.name) };
        if (stdJSON.attributes.title) { study.setTitle(stdJSON.attributes.title) };
        if (stdJSON.attributes.description) { study.setDescription(stdJSON.attributes.description) };
        if (stdJSON.attributes.accesstype) { study.setAccesstype(stdJSON.attributes.accesstype) };
        if (stdJSON.attributes.starts_at) { study.setStarts_at(stdJSON.attributes.starts_at) };
        if (stdJSON.attributes.ends_at) { study.setEnds_at(stdJSON.attributes.ends_at) };
        if (stdJSON.attributes.is_private) { study.setIs_private(stdJSON.attributes.is_private) };
        (stdJSON.attributes.is_running) ? study.setIs_running(true) : study.setIs_running(false);
        retArr.push(study);
      }

      // Setting all Vars
      this.allGroups = this.allGroups.concat(retArr);
      if (currentPage >= total) {
        this.allGroupsThreshold = new Date().getTime() + this.allGroupsAddTime;
        this.allGroupsLoaded = true;
      } else {
        this.getAllGroups((currentPage + 1));
      }
      this.allGroupsReplay.next(retArr);
    });

    return this.allGroupsReplay;
  }

  /**
   * This function returns myGroups for a spezific user.
   * @return ReplaySubject<any> - can contain error
   */
  public getMyGroups(currentPage: number = 1): Subject<any> {
    if (this.myGroupsReplay === null) {
      this.myGroupsReplay = new Subject();
    }

    if (this.myGroupsLoaded && this.myGroupsThreshold > (new Date().getTime())) {
      const tempReplay = new ReplaySubject(1);
      tempReplay.next(this.myGroups);
      return tempReplay;
    }

    // No threshold data found, the data will be fetched from api
    const request = this.communication.get(this.myGroupsUrl + '?page=' + currentPage + '&limit=0');
    request.subscribe(json => {
      // If an Error returned?
      if (json instanceof BasicError) {
        this.myGroupsThreshold = 0;
        this.myGroupsReplay.next(json);
        return;
      }

      // No Error occured
      this.amountMyGroups = json.meta.pagination.total;
      const total: number = json.meta.pagination.total_pages;
      const retArr: Study[] = [];


      for (let i = 0; i < json.data.length; i++) {
        const stdJSON = json.data[i];
        const study = new Study(stdJSON.id);
        if (stdJSON.id) { study.setId(stdJSON.id) };
        if (stdJSON.attributes.name) { study.setName(stdJSON.attributes.name) };
        if (stdJSON.attributes.title) { study.setTitle(stdJSON.attributes.title) };
        if (stdJSON.attributes.description) { study.setDescription(stdJSON.attributes.description) };
        if (stdJSON.attributes.accesstype) { study.setAccesstype(stdJSON.attributes.accesstype) };
        if (stdJSON.attributes.starts_at) { study.setStarts_at(stdJSON.attributes.starts_at) };
        if (stdJSON.attributes.ends_at) { study.setEnds_at(stdJSON.attributes.ends_at) };
        if (stdJSON.attributes.is_private) { study.setIs_private(stdJSON.attributes.is_private) };
        (stdJSON.attributes.is_running) ? study.setIs_running(true) : study.setIs_running(false);
        retArr.push(study);
      }

      // Setting all Vars
      this.myGroups = this.myGroups.concat(retArr);
      if (currentPage >= total) {
        this.myGroupsThreshold = new Date().getTime() + this.myGroupsAddTime;
        this.myGroupsLoaded = true;
        this.myGroupsReplay.next(retArr);
        this.myGroupsReplay = null;
      } else {
        this.getMyGroups((currentPage + 1));
        this.myGroupsReplay.next(retArr);
      }
    });

    return this.myGroupsReplay;
  }


  /**
   * Getting a specific group with the given id
   * @param id id of group
   */
  public getGroupDetail(id: String) {
    const replay = new Subject();

    // No threshold data found, the data will be fetched from api
    const request = this.communication.get(this.groupDetailsUrl + id);
    request.subscribe(json => {
      // If an Error returned?
      if (json instanceof BasicError) {
        replay.next(json);
        return;
      }

      // No Error occured
      const stdJSON = json.data;
      const study = new Study(stdJSON.id);

      if (stdJSON.attributes.name) { study.setName(stdJSON.attributes.name) };
      if (stdJSON.attributes.title) { study.setTitle(stdJSON.attributes.title) };
      if (stdJSON.attributes.description) { study.setDescription(stdJSON.attributes.description) };
      if (stdJSON.attributes.accesstype) { study.setAccesstype(stdJSON.attributes.accesstype) };
      if (stdJSON.attributes.starts_at) { study.setStarts_at(stdJSON.attributes.starts_at) };
      if (stdJSON.attributes.ends_at) { study.setEnds_at(stdJSON.attributes.ends_at) };
      if (stdJSON.attributes.is_private) { study.setIs_private(stdJSON.attributes.is_private) };
      (stdJSON.attributes.is_running) ? study.setIs_running(true) : study.setIs_running(false);

      replay.next(study);
    });

    return replay;
  }

  /**
   * Function to subscribe User to a Group
   * @param studyid
   * @param atype
   * @param pw
   */
  public subscribeToGroup(studyid: String, atype: String, pw: String = null): Subject<any> {
    const retReplay = new Subject();

    const payload = {
      'data': {
        'type': 'users',
        'attributes': {
        }
      }
    };

    // Compare data and add it to payload
    if (atype === 'password') {
      payload.data.attributes['password'] = pw.toString();
    } else {
      payload.data.attributes['code'] = null;
    }

    // Sending post http request to api
    this.communication.post(this.subscribeToGroupUrl.replace('##id##', studyid.toString()), payload).subscribe(data => {
      if (data instanceof BasicError) {
        console.log('--------GroupService---------: Failed to subscribe to Group.');
        console.log(data);
        retReplay.next(data);
      } else {
        retReplay.next(true);
      }
    });

    return retReplay;
  }


  public unsubscribeFromGroup(studyid: String): Subject<any> {
    const retReplay = new Subject();

    // Sending delete http request to api
    this.communication.delete(this.unsubscribeFromGroupUrl.replace('##id##', studyid.toString())).subscribe(data => {
      if (data instanceof BasicError) {
        console.log('--------GroupService---------: Failed to unsubscribe from Group with id: ' + studyid);
        retReplay.next(data);
      } else {
        retReplay.next(true);
      }
    });

    return retReplay;
  }


  /**
   * This function gets all collaborators of study
   * @param study Study to get collaborators from
   */
  public getStudyCollaborators(study: Study): Subject<any> {
    const retReplay = new Subject();

    this.communication.get(this.studyCollaboratorsURL.replace('##id##', study.getId().toString())).subscribe(json => {
      if (json instanceof BasicError) {
        console.log('-----GroupService-----: Failed to get collaborators of study.');
        retReplay.next(json);
        return;
      }

      // Getting all collaborators of this study from json
      const includedData = json.included;

      if (includedData instanceof Array) {
        const users = [];

        for (const user of includedData) {
          if (user.type !== 'users') {
            continue; // if included data isn't type of users go to next object
          }

          const usr = new User(user.id);

          if (user.attributes.name !== undefined) {
            usr.setName(user.attributes.name);
          }

          if (user.attributes.email !== undefined) {
            usr.setEmail(user.attributes.email);
          }

          if (user.attributes.firstname !== undefined) {
            usr.setFirstName(user.attributes.firstname);
          }

          if (user.attributes.lastname !== undefined) {
            usr.setLastName(user.attributes.lastname);
          }

          if (user.attributes.picture !== undefined) {
            usr.setPicture(user.attributes.picture);
          }

          users.push(usr);
        }

        retReplay.next(users);
      } else {
        retReplay.next([]);
      }
    });

    return retReplay;
  }


  /**
   * This function gets all collaborators of study
   * @param studies studies to get collaborators from
   */
  public getCollaboratorsOfStudies(studies: Array<Study>): Subject<any> {
    const retReplay = new Subject();

    const users = [];
    const usersMap = {};
    let count = 0;

    for (const study of studies) {
      this.getStudyCollaborators(study).subscribe(usrs => {
        if (usrs instanceof BasicError) {
          count = count + 1;
        } else {
          for (const usr of usrs) {
            if (usersMap[usr.getID()] === undefined) {
              users.push(usr);
              usersMap[usr.getID().toString()] = true;
            }
          }
          count = count + 1;
        }

        if (count >= studies.length) {
          retReplay.next(users);
        }
      });
    }

    return retReplay;
  }

}



// WEBPACK FOOTER //
// C:/Users/andyw/Documents/Uni/Master/Masterprojekt/Angular/src/app/services/group.service.ts